/**
 * Curated, per-ANIMAL descriptor chips a producer can self-assert on one of
 * their individual animals. Selection is stored on `Animal.descriptorChips` as
 * an array of the stable `value` codes below; the `label` is what renders on
 * the buyer listing card and animal-detail page.
 *
 * Unlike the org-level `BUYER_DESCRIPTOR_CHIPS` (which deliberately EXCLUDES
 * self-asserted marketing/regulatory claims), this per-animal catalog is a
 * separate, more permissive list: producers may claim diet/raising/breed/meat-
 * quality descriptors ("Grass-fed", "Wagyu", "Well-marbled", …) about the
 * specific animal. These are self-asserted claims — the platform surfaces them
 * as the producer's own assertions, not verified facts.
 *
 * v1 starter set — the platform team should review/expand this before a wider
 * launch. Because only the stable `value` codes are persisted, labels can be
 * reworded later without a data migration; do NOT change an existing `value`
 * (it would orphan stored selections).
 */
export interface AnimalDescriptorChip {
  /** Stable persisted code. Never change once shipped. */
  value: string;
  /** Human-facing label rendered on the buyer card + detail page + editor. */
  label: string;
  /** Grouping bucket for the picker UI (not persisted). */
  category: string;
}

/**
 * Species-agnostic attribute chips, offered for EVERY species. Split by
 * category so `animalDescriptorChipsForSpecies` can splice the species' breed
 * list into the catalog's stable category order (Diet → Raising → Breed →
 * Meat quality).
 */
const DIET_CHIPS: readonly AnimalDescriptorChip[] = [
  { value: 'grassFed', label: 'Grass-fed', category: 'Diet' },
  { value: 'grainFinished', label: 'Grain-finished', category: 'Diet' },
  { value: 'grassFinished', label: 'Grass-finished', category: 'Diet' },
  { value: 'pastureRaised', label: 'Pasture-raised', category: 'Diet' },
  { value: 'hayFed', label: 'Hay-fed', category: 'Diet' },
];

const RAISING_CHIPS: readonly AnimalDescriptorChip[] = [
  { value: 'noAddedHormones', label: 'No added hormones', category: 'Raising' },
  { value: 'noAntibiotics', label: 'No antibiotics', category: 'Raising' },
  { value: 'freeRange', label: 'Free-range', category: 'Raising' },
  { value: 'heritageBreed', label: 'Heritage breed', category: 'Raising' },
];

/** Breed chips every species offers (in addition to its own breed list). */
const GENERIC_BREED_CHIPS: readonly AnimalDescriptorChip[] = [
  { value: 'crossBred', label: 'Cross-bred', category: 'Breed' },
];

const MEAT_QUALITY_CHIPS: readonly AnimalDescriptorChip[] = [
  { value: 'lean', label: 'Lean', category: 'Meat quality' },
  { value: 'wellMarbled', label: 'Well-marbled', category: 'Meat quality' },
  { value: 'dryAged', label: 'Dry-aged', category: 'Meat quality' },
];

const breed = (value: string, label: string): AnimalDescriptorChip => ({
  value,
  label,
  category: 'Breed',
});

/**
 * Per-species breed vocabularies, keyed by the lowercase `EAnimalSpecies`
 * value (`hog` carries pork breeds). Pre-existing beef breed codes (`wagyu`,
 * `angus`, `hereford`) keep their original camelCase-era values; new
 * multi-word codes are kebab-case. A code may legitimately appear under more
 * than one species (e.g. Hampshire is both a hog and a sheep breed; Angus
 * calves are raised for veal) — the union catalog de-duplicates by value.
 */
export const SPECIES_BREED_DESCRIPTOR_CHIPS: Record<
  string,
  readonly AnimalDescriptorChip[]
> = {
  beef: [
    breed('angus', 'Angus'),
    breed('wagyu', 'Wagyu'),
    breed('hereford', 'Hereford'),
    breed('charolais', 'Charolais'),
    breed('highland', 'Highland'),
    breed('longhorn', 'Longhorn'),
    breed('brahman', 'Brahman'),
    breed('simmental', 'Simmental'),
  ],
  hog: [
    breed('berkshire', 'Berkshire'),
    breed('duroc', 'Duroc'),
    breed('mangalitsa', 'Mangalitsa'),
    breed('tamworth', 'Tamworth'),
    breed('hampshire', 'Hampshire'),
    breed('yorkshire', 'Yorkshire'),
    breed('gloucestershire-old-spot', 'Gloucestershire Old Spot'),
  ],
  lamb: [
    breed('dorper', 'Dorper'),
    breed('katahdin', 'Katahdin'),
    breed('suffolk', 'Suffolk'),
    breed('icelandic', 'Icelandic'),
    breed('merino', 'Merino'),
    breed('hampshire', 'Hampshire'),
  ],
  goat: [
    breed('boer', 'Boer'),
    breed('kiko', 'Kiko'),
    breed('spanish', 'Spanish'),
    breed('nubian', 'Nubian'),
  ],
  bison: [
    breed('plains-bison', 'Plains bison'),
    breed('wood-bison', 'Wood bison'),
  ],
  // `venison` is deer. The former `elk` entry here moved out when elk became a
  // species in its own right — offering "Elk" as a deer *breed* alongside an
  // Elk *species* is a coin-flip for whoever is picking. The code itself lives
  // on in {@link RETIRED_BREED_CHIPS} so already-saved animals keep it.
  venison: [
    breed('whitetail', 'Whitetail'),
    breed('mule-deer', 'Mule deer'),
    breed('red-deer', 'Red deer'),
    breed('fallow-deer', 'Fallow deer'),
    breed('axis-deer', 'Axis deer'),
  ],
  elk: [
    breed('rocky-mountain-elk', 'Rocky Mountain elk'),
    breed('roosevelt-elk', 'Roosevelt elk'),
    breed('tule-elk', 'Tule elk'),
  ],
  moose: [
    breed('shiras-moose', 'Shiras moose'),
    breed('canada-moose', 'Canada moose'),
    breed('alaska-yukon-moose', 'Alaska-Yukon moose'),
  ],
  antelope: [breed('pronghorn', 'Pronghorn')],
  bear: [breed('black-bear', 'Black bear'), breed('brown-bear', 'Brown bear')],
  yak: [breed('tibetan-yak', 'Tibetan yak')],
  veal: [
    breed('holstein', 'Holstein'),
    breed('jersey', 'Jersey'),
    breed('angus', 'Angus'),
  ],
  alligator: [breed('american-alligator', 'American alligator')],
};

/**
 * The catalog offered for one species: generic Diet + Raising attributes, the
 * species' breed list (plus the generic breed chips like "Cross-bred"), then
 * generic Meat-quality attributes. An unknown/missing species yields the
 * generic categories only (no species breeds).
 * @param species
 */
export function animalDescriptorChipsForSpecies(
  species?: string | null,
): AnimalDescriptorChip[] {
  const speciesBreeds = species
    ? (SPECIES_BREED_DESCRIPTOR_CHIPS[species.toLowerCase()] ?? [])
    : [];
  return [
    ...DIET_CHIPS,
    ...RAISING_CHIPS,
    ...speciesBreeds,
    ...GENERIC_BREED_CHIPS,
    ...MEAT_QUALITY_CHIPS,
  ];
}

function dedupeByValue(
  chips: readonly AnimalDescriptorChip[],
): AnimalDescriptorChip[] {
  const seen = new Set<string>();
  return chips.filter((c) => {
    if (seen.has(c.value)) return false;
    seen.add(c.value);
    return true;
  });
}

/**
 * Breed codes no longer OFFERED for any species, but still valid so that
 * animals already carrying them keep them.
 *
 * This distinction is load-bearing: {@link ANIMAL_DESCRIPTOR_CHIP_VALUES} is
 * derived from the catalog below and used by
 * {@link sanitizeAnimalDescriptorChips} as the write-validation set. Animal
 * updates re-sanitize the WHOLE chip array, so a code dropped from the catalog
 * is not merely hidden — it is silently stripped from an existing animal the
 * next time anything about it is saved. Retiring a code means moving it here,
 * never deleting it.
 *
 * - `elk`: was a `venison` breed before Elk became its own species.
 */
const RETIRED_BREED_CHIPS: readonly AnimalDescriptorChip[] = [
  breed('elk', 'Elk'),
];

/**
 * UNION of all vocabularies (generic attributes + every species' breeds, plus
 * retired codes), de-duplicated by value. Used for validation on write and for
 * label resolution on buyer-facing display, so a chip saved under any species
 * (or a legacy chip from before per-species vocabularies) always resolves to a
 * human label regardless of where it renders.
 */
export const ANIMAL_DESCRIPTOR_CHIPS: readonly AnimalDescriptorChip[] =
  dedupeByValue([
    ...DIET_CHIPS,
    ...RAISING_CHIPS,
    ...Object.values(SPECIES_BREED_DESCRIPTOR_CHIPS).flat(),
    ...GENERIC_BREED_CHIPS,
    ...RETIRED_BREED_CHIPS,
    ...MEAT_QUALITY_CHIPS,
  ]);

/** The maximum number of descriptor chips a producer may put on one animal. */
export const MAX_ANIMAL_DESCRIPTOR_CHIPS = 5;

/** The maximum length (chars) of the free-text producer description. */
export const MAX_ANIMAL_DESCRIPTION_LENGTH = 600;

/** Set of valid chip codes for fast membership/validation checks. */
export const ANIMAL_DESCRIPTOR_CHIP_VALUES = new Set(
  ANIMAL_DESCRIPTOR_CHIPS.map((c) => c.value),
);

/**
 * Display label for a chip code; falls back to the code itself when unknown
 * (so an orphaned/legacy code still renders something readable).
 * @param code
 */
export function animalDescriptorChipLabel(code: string): string {
  return ANIMAL_DESCRIPTOR_CHIPS.find((c) => c.value === code)?.label ?? code;
}

/**
 * Normalize a raw chip selection from a client: keeps only known codes,
 * de-duplicates (preserving order), and caps at
 * {@link MAX_ANIMAL_DESCRIPTOR_CHIPS}. A non-array input yields `[]`. Shared by
 * the update mutation/service (authoritative) so callers can't persist invalid,
 * duplicate, or over-cap selections.
 * @param input
 */
export function sanitizeAnimalDescriptorChips(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input) {
    if (typeof raw !== 'string') continue;
    if (ANIMAL_DESCRIPTOR_CHIP_VALUES.has(raw) && !seen.has(raw)) {
      seen.add(raw);
      result.push(raw);
    }
    if (result.length >= MAX_ANIMAL_DESCRIPTOR_CHIPS) break;
  }
  return result;
}

/**
 * Normalize a raw free-text description: coerce to string, trim, and cap at
 * {@link MAX_ANIMAL_DESCRIPTION_LENGTH} chars (hard truncate). An empty (or
 * whitespace-only) result yields `undefined` so the field is cleared rather
 * than stored as an empty string.
 * @param input
 */
export function sanitizeAnimalDescription(input: unknown): string | undefined {
  if (input == null) return undefined;
  const str = typeof input === 'string' ? input : String(input);
  const trimmed = str.trim();
  if (trimmed === '') return undefined;
  return trimmed.slice(0, MAX_ANIMAL_DESCRIPTION_LENGTH);
}
